import { Injectable } from "@angular/core";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
// real service imports
import { CcyLookupService } from "../../lookup/model/impl/CcyLookupService";
import { CcyPairLookupService } from "../../lookup/model/impl/CcyPairLookupService";
import { ClientLookupService } from "../../lookup/model/impl/ClientLookupService";
import { AccountLookupService } from "../../lookup/model/impl/AccountLookupService";
import { TraderGroupLookupService } from "../../lookup/model/impl/TraderGroupLookupService";
import { SalesGroupLookupService } from "../../lookup/model/impl/SalesGroupLookupService";
import { UserLookupService } from "../../lookup/model/impl/UserLookupService";
import { TranslateService } from "@ngx-translate/core";
import { TradeDeal } from "../../model/core/TradeDeal";
import { TradeRate } from "../../model/core/TradeRate";
import { TradeRateGroupType } from "../../model/core/TradeRateGroupType";
import { StaticDataPrivatizationType } from "../../model/privatization/enum/StaticDataPrivatizationType";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

@Injectable()
export class TradeDealAdapter {
  constructor(
    private _ccyLookupService: CcyLookupService,
    private _ccyPairLookupService: CcyPairLookupService,
    private _clientLookup: ClientLookupService,
    private _accountLookup: AccountLookupService,
    private _salesGroupLookup: SalesGroupLookupService,
    private _traderGroupLookup: TraderGroupLookupService,
    private _userLookup: UserLookupService,
    private _translationService: TranslateService,
  ) {}

  public transformDeal(deal: CubiqTS.DealImpl): TradeDeal {
    let result: TradeDeal = null;

    if (deal) {
      result = new TradeDeal();
      // IDs
      result.dealId = deal.id.toString(); ///<number>(deal.id as CubiqTS.Long).getLowBits();
      result.dealRef = deal.dealRef;
      result.orderId = deal.orderId.toString();
      result.orderRef = deal.orderRef;
      result.clOrderId = deal.clOrdId ? deal.clOrdId : null;
      // product
      result.baseCcy = deal.baseCcyCd;
      result.nonBaseCcy = deal.nonBaseCcyCd;
      result.isInvert = deal.isInvert;
      result.instrument = deal.isInvert
        ? deal.nonBaseCcyCd + "/" + deal.baseCcyCd
        : deal.baseCcyCd + "/" + deal.nonBaseCcyCd;
      result.productType = deal.productType; // result.productType = deal.productId;
      result.orderTypeId = deal.orderType.toString();
      result.orderTypeName = this.resolveOrderType(deal.orderType, deal.algoStrategyName);
      result.strategyId = deal.strategyId ? (deal.strategyId as CubiqTS.Long).getLowBits() : null; // DealImpl: strategyId;
      result.strategyName = null; // note: empty for now
      result.dealDir = deal.isBuy ? deal.isBuy : null; // DealImpl: isBuy; / true = buy; false = sell;
      result.dealDirStr =
        deal.requestOrderType == CubiqTS.RequestOrderType.CALL
          ? CubiqTS.RequestOrderType[CubiqTS.RequestOrderType.CALL]
          : deal.isBuy
          ? "Buy"
          : "Sell";
      result.dealStateId = deal.dealStateId.valueOf(); // DealImpl: dealStateId; / ENUM(ACTIVE, FILL_ERROR, COMPLETED, CANCELLED, ACK_TIME_OUT, NAK, DONE_NO_STP, STP_HOLD)
      result.dealStateName = this.resolveDealState(deal.dealStateId.valueOf()); // Not in DealImpl
      result.dealtCcy = deal.dealtCcy ? deal.dealtCcy : null;
      result.assetClass = "FX"; // 'FX'
      result.fixingCode = deal.fixingCode ? deal.fixingCode : null; // DealImpl: fixingCode;
      // users
      result.entryUserId = deal.entryUserId ? deal.entryUserId.toString() : null; // DealImpl: entryUserId;
      result.entryUserName = deal.entryUserId ? this.lookupUser(deal.entryUserId, true) : null; // Not in DealImpl / note: resolve with userLookup
      result.fillUserId = null; // no lookup implemented yet
      result.fillUserName = deal.fillUserName ? deal.fillUserName : null; // DealImpl: fillUserName;
      result.fillUserCode = deal.fillUserCode ? deal.fillUserCode : null; // DealImpl: fillUserName;
      result.confirmUserId = deal.confirmedSalesUserId
        ? (deal.confirmedSalesUserId as CubiqTS.Long).getLowBits()
        : null; // DealImpl: confirmedSalesUserId;
      result.confirmUserName = deal.confirmedSalesUserId
        ? this.lookupUser(deal.confirmedSalesUserId, true)
        : null; // Not in DealImpl / note: resolve with userLookup
      // groups
      result.traderGroupId = deal.traderGroupId
        ? (deal.traderGroupId as CubiqTS.Long).getLowBits()
        : null; // DealImpl: traderGroupId;
      result.traderGroupName = deal.traderGroupId
        ? this.lookupTraderGroup(deal.traderGroupId)
        : null;
      result.salesGroupId = deal.salesGroupId
        ? (deal.salesGroupId as CubiqTS.Long).getLowBits()
        : null; // DealImpl: salesGroupId;
      result.salesGroupName = deal.salesGroupId ? this.lookupSalesGroup(deal.salesGroupId) : null;
      // client
      if (StaticDataPrivatizationType.hasPrivatization(deal)) {
        let client = deal.clientCode ? this.lookupClient(deal.clientCode) : null;
        result.clientId = client ? client.clientId : null; // DealImpl: clientCode;
        result.clientName = client ? client.clientName : null;
      } else {
        result.clientId = deal.clientCode; // DealImpl: clientCode;
        result.clientName = deal.clientName;
      }
      result.clientBranchCode = deal.clientBranchCode ? deal.clientBranchCode : null; // DealImpl: clientBranchCode;
      // account
      if (StaticDataPrivatizationType.hasPrivatization(deal)) {
        let account = deal.accountCode ? this.lookupAccount(deal.accountCode) : null;
        result.accountId = account ? account.id : null; // DealImpl: accountCode;
        result.accountName = account ? account.name : null;
        result.accountBookingType = deal.accountCode
          ? this.lookupAccountBookingType(deal.accountCode)
          : null;
        result.accountBookingCd = account ? account.bookingCode : null;
        result.accountContact = account ? account.contact : null;
      } else {
        result.accountId = deal.accountCode; // DealImpl: clientCode;
        result.accountName = deal.accountName;
        if (
          deal.counterPartyDetails &&
          deal.counterPartyDetails.account instanceof CubiqTS.AccountImpl
        ) {
          let account: CubiqTS.AccountImpl = deal.counterPartyDetails.account;
          result.accountBookingType = CubiqTS.AccountBookingType[account.bookingType];
          result.accountBookingCd = account ? account.bookingCode : null;
          result.accountContact = account ? account.contact : null;
        }
      }

      // rates
      result.orderRate = deal.requestedPx ? deal.requestedPx.toNumber() : null; // DealImpl: requestedPx;
      result.marketSpot = deal.spotMarketRate ? deal.spotMarketRate.toNumber() : null; // DealImpl: spotMarketRate;
      result.marketPts = deal.forwardMarketPoints ? deal.forwardMarketPoints.toNumber() : null; // DealImpl: forwardMarketPoints;
      result.marketAllIn = deal.forwardMarketRate ? deal.forwardMarketRate.toNumber() : null; // DealImpl: forwardMarketRate;
      result.clientSpot = deal.spotClientRate ? deal.spotClientRate.toNumber() : null; // DealImpl: spotClientRate;
      result.clientPts = deal.forwardPoints ? deal.forwardPoints.toNumber() : null; // DealImpl: forwardPoints;
      result.clientAllIn = deal.allInClientRate ? deal.allInClientRate.toNumber() : null; // DealImpl: allInClientRate;
      // rates formatted as string (when needed)
      result.orderRateStr = deal.requestedPx
        ? this.formatRateValue(
            deal.requestedPx,
            deal.baseCcyCd,
            deal.nonBaseCcyCd,
            deal.isInvert,
            deal.productType,
            deal.orderType,
          )
        : null; // DealImpl: requestedPx;
      result.marketSpotStr = deal.spotMarketRate
        ? this.formatRateValue(
            deal.spotMarketRate,
            deal.baseCcyCd,
            deal.nonBaseCcyCd,
            deal.isInvert,
            deal.productType,
            deal.orderType,
          )
        : null; // DealImpl: spotMarketRate;
      result.marketPtsStr = deal.forwardMarketPoints
        ? this.formatRateValue(
            deal.forwardMarketPoints,
            deal.baseCcyCd,
            deal.nonBaseCcyCd,
            deal.isInvert,
            deal.productType,
            deal.orderType,
          )
        : null; // DealImpl: forwardMarketPoints;
      result.marketAllInStr = deal.forwardMarketRate
        ? this.formatRateValue(
            deal.forwardMarketRate,
            deal.baseCcyCd,
            deal.nonBaseCcyCd,
            deal.isInvert,
            deal.productType,
            deal.orderType,
          )
        : null; // DealImpl: forwardMarketRate;
      result.clientSpotStr = deal.spotClientRate
        ? this.formatRateValue(
            deal.spotClientRate,
            deal.baseCcyCd,
            deal.nonBaseCcyCd,
            deal.isInvert,
            deal.productType,
            deal.orderType,
          )
        : null; // DealImpl: spotClientRate;
      result.clientPtsStr = deal.forwardPoints
        ? this.formatRateValue(
            deal.forwardPoints,
            deal.baseCcyCd,
            deal.nonBaseCcyCd,
            deal.isInvert,
            deal.productType,
            deal.orderType,
          )
        : null; // DealImpl: forwardPoints;
      result.clientAllInStr = deal.allInClientRate
        ? this.formatRateValue(
            deal.allInClientRate,
            deal.baseCcyCd,
            deal.nonBaseCcyCd,
            deal.isInvert,
            deal.productType,
            deal.orderType,
          )
        : null; // DealImpl: allInClientRate;
      // amounts
      result.dealtAmt = deal.amt1 ? deal.amt1.toNumber() : null; // DealImpl: amt1;
      result.dealtAmtUsd = deal.usdEquivAmt ? deal.usdEquivAmt.toNumber() : null; // DealImpl: usdEquivAmt;
      result.counterAmt = deal.amt2 ? deal.amt2.toNumber() : null; // amt2

      result.dealtAmtStr =
        deal.requestOrderType == CubiqTS.RequestOrderType.CALL
          ? "-"
          : deal.amt1
          ? this.formatAmtValue(deal.amt1, deal.dealtCcy)
          : null; // DealImpl: amt1;
      result.dealtAmtUsdStr =
        deal.requestOrderType == CubiqTS.RequestOrderType.CALL
          ? "-"
          : deal.usdEquivAmt
          ? this.formatAmtValue(deal.usdEquivAmt, "USD")
          : null; // DealImpl: usdEquivAmt;
      result.counterAmtStr =
        deal.requestOrderType == CubiqTS.RequestOrderType.CALL
          ? "-"
          : deal.amt2
          ? this.formatAmtValue(
              deal.amt2,
              this.reverseCcy(deal.dealtCcy, deal.baseCcyCd, deal.nonBaseCcyCd),
            )
          : null; // amt2

      // dates  / timestamp in milliseconds
      result.tradeDate = deal.tradeDate.valueOf();
      result.valueDate = deal.valueDate.valueOf();
      result.fixingDate = deal.fixingDate.valueOf();

      let valueDate: string = null;
      try {
        result.bmarkFixingDateTime = CubiqTS.moment.isMoment(deal.fixingDate)
          ? deal.fixingDate.format("DD-MMM-YY HH:mm")
          : null;
        valueDate = CubiqTS.moment.isMoment(deal.valueDate)
          ? deal.valueDate.format("DD-MMM-YY")
          : null;
      } catch (ex) {
        result.bmarkFixingDateTime = null;
        valueDate = null;
      }

      result.bmarkValueDateTenor =
        deal.valueDateTenor != null
          ? deal.valueDateTenor + (valueDate != null ? " (" + valueDate + ")" : "")
          : valueDate != null
          ? valueDate
          : null;
      result.valueDateTenor = deal.valueDateTenor;

      result.creationDate = deal.creationDate.valueOf();
      result.entryDate = deal.entryDate.valueOf();

      // comments
      result.fillComment = deal.fillComment ? deal.fillComment : null;
      result.clientComment = deal.clientComment ? deal.clientComment : null;
      result.privateComment = deal.privateComment ? deal.privateComment : null;

      result.holdStp = deal.holdStp;

      result.marketRateAtFill =
        (deal.marketBidRateAtFill
          ? this.formatRateValue(
              deal.marketBidRateAtFill,
              deal.baseCcyCd,
              deal.nonBaseCcyCd,
              deal.isInvert,
              deal.productType,
              deal.orderType,
            )
          : "-") +
        "/" +
        (deal.marketOfferRateAtFill
          ? this.formatRateValue(
              deal.marketOfferRateAtFill,
              deal.baseCcyCd,
              deal.nonBaseCcyCd,
              deal.isInvert,
              deal.productType,
              deal.orderType,
            )
          : "-");

      result.manualDealRef = deal.manualDealReference;

      result.triggerTime = deal.triggerTime ? deal.triggerTime.valueOf() : 0;

      result.triggerRate =
        deal.triggerSide === CubiqTS.RateSideType.BID
          ? deal.triggerBidRate
            ? this.formatRateValue(
                deal.triggerBidRate,
                deal.baseCcyCd,
                deal.nonBaseCcyCd,
                deal.isInvert,
                deal.productType,
                deal.orderType,
              )
            : ""
          : deal.triggerOfferRate
          ? this.formatRateValue(
              deal.triggerOfferRate,
              deal.baseCcyCd,
              deal.nonBaseCcyCd,
              deal.isInvert,
              deal.productType,
              deal.orderType,
            )
          : "";
      result.dealConfirmations = deal.dealConfirmations;

      result.tradeRates = this.createComponentRates(deal);

      result.oboClient = deal.oboClient != null ? deal.oboClient : "-";
      result.oboAccount = deal.oboAccount != null ? deal.oboAccount : "-";

      result.lastMkt = deal.lastMkt;

      result.customValues = deal.customValues;
    }

    return result;
  }

  private resolveOrderType(orderType: number, algoStrategyName: string): string {
    if (orderType !== undefined && orderType != null) {
      switch (orderType) {
        case CubiqTS.OrderType.RESERVED1:
          return "RES-1";
        case CubiqTS.OrderType.LIMIT_TP:
          return "TP";
        case CubiqTS.OrderType.LIMIT_SL:
          return "SL";
        case CubiqTS.OrderType.LIMIT_CALL:
          return "CALL";
        case CubiqTS.OrderType.ALGO:
          return algoStrategyName;
        case CubiqTS.OrderType.RESERVED3:
          return "SL";
        case CubiqTS.OrderType.BENCHMARK:
          return "BENCHMARK";
        case CubiqTS.OrderType.MARKET:
        case CubiqTS.OrderType.MARKET_IOC:
        case CubiqTS.OrderType.MARKET_FOK:
          return "MARKET";
      }
    }
  }

  private resolveDealState(dealStateId: number): string {
    switch (dealStateId) {
      case CubiqTS.OrderDealState.ACTIVE:
        return this._translationService.instant("OrderDealState.ACTIVE");
      case CubiqTS.OrderDealState.FILL_ERROR:
        return this._translationService.instant("OrderDealState.FILL_ERROR");
      case CubiqTS.OrderDealState.COMPLETED:
        return this._translationService.instant("OrderDealState.COMPLETED");
      case CubiqTS.OrderDealState.CANCELLED:
        return this._translationService.instant("OrderDealState.CANCELLED");
      case CubiqTS.OrderDealState.ACK_TIME_OUT:
        return this._translationService.instant("OrderDealState.ACK_TIME_OUT");
      case CubiqTS.OrderDealState.NAK:
        return this._translationService.instant("OrderDealState.NAK");
      case CubiqTS.OrderDealState.DONE_NO_STP:
        return this._translationService.instant("OrderDealState.DONE_NO_STP");
      case CubiqTS.OrderDealState.STP_HOLD:
        return this._translationService.instant("OrderDealState.STP_HOLD");
      case CubiqTS.OrderDealState.FILL_PENDING:
        return this._translationService.instant("OrderDealState.FILL_PENDING");
      case CubiqTS.OrderDealState.FILL_CANCEL:
        return this._translationService.instant("OrderDealState.FILL_CANCEL");
      default: {
        return "Part Filled";
      }
    }
  }

  private createComponentRates(deal: CubiqTS.DealImpl): Array<TradeRate> {
    let comps: Array<TradeRate> = [];
    let clientRate: TradeRate = new TradeRate();
    clientRate.grouping = TradeRateGroupType.CLIENT_RATES;
    clientRate.groupingStr = this._translationService.instant(
      "TRADE_RATE_GROUP." + TradeRateGroupType[clientRate.grouping],
    );
    clientRate.instrument = this.getDealInstrument(
      deal.baseCcyCd,
      deal.nonBaseCcyCd,
      deal.isInvert,
    );
    clientRate.spotRate = deal.spotClientRate
      ? this.formatRateValue(
          deal.spotClientRate,
          deal.baseCcyCd,
          deal.nonBaseCcyCd,
          deal.isInvert,
          deal.productType,
          deal.orderType,
        )
      : null; // DealImpl: spotClientRate;
    clientRate.fwdPts = deal.forwardPoints
      ? this.formatRateValue(
          deal.forwardPoints,
          deal.baseCcyCd,
          deal.nonBaseCcyCd,
          deal.isInvert,
          deal.productType,
          deal.orderType,
        )
      : null; // DealImpl: forwardPoints;
    clientRate.allIn = deal.allInClientRate
      ? this.formatRateValue(
          deal.allInClientRate,
          deal.baseCcyCd,
          deal.nonBaseCcyCd,
          deal.isInvert,
          deal.productType,
          deal.orderType,
        )
      : null; // DealImpl: allInClientRate;

    comps.push(clientRate);

    let salesMarkupRate: TradeRate = new TradeRate();
    salesMarkupRate.grouping = TradeRateGroupType.SALES_MARKUP;
    salesMarkupRate.groupingStr = this._translationService.instant(
      "TRADE_RATE_GROUP." + TradeRateGroupType[salesMarkupRate.grouping],
    );
    salesMarkupRate.instrument = this.getDealInstrument(
      deal.baseCcyCd,
      deal.nonBaseCcyCd,
      deal.isInvert,
    );
    salesMarkupRate.spotRate = deal.spotSalesMargin
      ? this.formatRateValue(
          deal.spotSalesMargin,
          deal.baseCcyCd,
          deal.nonBaseCcyCd,
          deal.isInvert,
          deal.productType,
          deal.orderType,
        )
      : null;
    salesMarkupRate.fwdPts = deal.forwardSalesMargin
      ? this.formatRateValue(
          deal.forwardSalesMargin,
          deal.baseCcyCd,
          deal.nonBaseCcyCd,
          deal.isInvert,
          deal.productType,
          deal.orderType,
        )
      : null;
    salesMarkupRate.allIn = deal.salesMargin
      ? this.formatRateValue(
          deal.salesMargin,
          deal.baseCcyCd,
          deal.nonBaseCcyCd,
          deal.isInvert,
          deal.productType,
          deal.orderType,
        )
      : null;
    comps.push(salesMarkupRate);

    let marketRate: TradeRate = new TradeRate();
    marketRate.grouping = TradeRateGroupType.MARKET_RATES;
    marketRate.groupingStr = this._translationService.instant(
      "TRADE_RATE_GROUP." + TradeRateGroupType[marketRate.grouping],
    );
    marketRate.instrument = this.getDealInstrument(
      deal.baseCcyCd,
      deal.nonBaseCcyCd,
      deal.isInvert,
    );
    marketRate.spotRate = deal.spotMarketRate
      ? this.formatRateValue(
          deal.spotMarketRate,
          deal.baseCcyCd,
          deal.nonBaseCcyCd,
          deal.isInvert,
          deal.productType,
          deal.orderType,
        )
      : null; // DealImpl: spotMarketRate;
    marketRate.fwdPts = deal.forwardMarketPoints
      ? this.formatRateValue(
          deal.forwardMarketPoints,
          deal.baseCcyCd,
          deal.nonBaseCcyCd,
          deal.isInvert,
          deal.productType,
          deal.orderType,
        )
      : null; // DealImpl: forwardMarketPoints;
    marketRate.allIn = deal.forwardMarketRate
      ? this.formatRateValue(
          deal.forwardMarketRate,
          deal.baseCcyCd,
          deal.nonBaseCcyCd,
          deal.isInvert,
          deal.productType,
          deal.orderType,
        )
      : null; // DealImpl: forwardMarketRate;
    marketRate.localBook = deal.localBookName;
    marketRate.remoteBook = deal.remoteBookName;
    comps.push(marketRate);

    if (deal.componentDetails) {
      deal.componentDetails.forEach((comp) => {
        marketRate = new TradeRate();
        marketRate.grouping = TradeRateGroupType.MARKET_RATES;
        marketRate.groupingStr = this._translationService.instant(
          "TRADE_RATE_GROUP." + TradeRateGroupType[marketRate.grouping],
        );
        marketRate.instrument = comp.baseCcyCd + "/" + comp.nonBaseCcyCd;
        marketRate.spotRate = comp.spotRt
          ? this.truncateRateValue(
              comp.spotRt.toNumber(),
              comp.baseCcyCd,
              comp.nonBaseCcyCd,
              false,
              deal.productType,
              deal.orderType,
            )
          : null; // DealImpl: spotMarketRate;
        marketRate.fwdPts = comp.outrightRt.minus(comp.spotRt)
          ? this.truncateRateValue(
              comp.outrightRt.minus(comp.spotRt).toNumber(),
              comp.baseCcyCd,
              comp.nonBaseCcyCd,
              false,
              deal.productType,
              deal.orderType,
            )
          : null; // DealImpl: forwardMarketPoints;
        marketRate.allIn = comp.outrightRt
          ? this.truncateRateValue(
              comp.outrightRt.toNumber(),
              comp.baseCcyCd,
              comp.nonBaseCcyCd,
              false,
              deal.productType,
              deal.orderType,
            )
          : null; // DealImpl: forwardMarketRate;
        marketRate.localBook = comp.localBookName;
        marketRate.remoteBook = comp.remoteBookName;
        comps.push(marketRate);
      });
    }
    /* NB : OMS-26580       
        let traderMarkupRate:TradeRate= new TradeRate();
        traderMarkupRate.grouping=TradeRateGroupType.TRADER_MARKUP;
        traderMarkupRate.groupingStr=this._translationService.instant("TRADE_RATE_GROUP."+TradeRateGroupType[traderMarkupRate.grouping]);
        traderMarkupRate.instrument= this.getDealInstrument(deal.baseCcyCd,deal.nonBaseCcyCd,deal.isInvert);
        traderMarkupRate.spotRate = deal.spotTraderMargin ? this.formatRateValue(deal.spotTraderMargin.toNumber(), deal.baseCcyCd, deal.nonBaseCcyCd, deal.isInvert) : null;
        traderMarkupRate.fwdPts = deal.forwardTraderMargin ? this.formatRateValue(deal.forwardTraderMargin.toNumber(), deal.baseCcyCd, deal.nonBaseCcyCd, deal.isInvert) : null;
        traderMarkupRate.allIn = deal.dealerMargin ? this.formatRateValue(deal.dealerMargin.toNumber(), deal.baseCcyCd, deal.nonBaseCcyCd, deal.isInvert) : null;
        comps.push(traderMarkupRate);
        if(deal.componentDetails){
            deal.componentDetails.forEach(comp => {
                traderMarkupRate= new TradeRate();
                traderMarkupRate.grouping=TradeRateGroupType.TRADER_MARKUP;
                traderMarkupRate.groupingStr=this._translationService.instant("TRADE_RATE_GROUP."+TradeRateGroupType[traderMarkupRate.grouping]);
                traderMarkupRate.instrument= comp.baseCcyCd+"/"+comp.nonBaseCcyCd;
                traderMarkupRate.allIn = comp.dealerMargin ? this.formatRateValue(comp.dealerMargin.toNumber(), comp.baseCcyCd, comp.nonBaseCcyCd,false) : null;
                comps.push(traderMarkupRate);
            });
        }
*/
    return comps;
  }

  private getDealInstrument(base: string, nonBase: string, isInvert: boolean): string {
    if (isInvert) return nonBase + "/" + base;
    return base + "/" + nonBase;
  }

  private lookupUser(username: string | CubiqTS.Long, showUserCode: boolean): string | null {
    if (this._userLookup) {
      let key: any = username;
      let userCode: boolean = typeof username === "string";
      let user: CubiqTS.UserImpl = userCode
        ? this._userLookup.getUser(key)
        : this._userLookup.getItem(key);

      if (user) {
        return showUserCode ? user.userCode : user.userName;
      }
    }
    return null;
  }

  private lookupAccount(accountCode: string): CubiqTS.Account | null {
    if (this._accountLookup) {
      let account = this._accountLookup.getAccount(accountCode);
      if (account) {
        return account;
      }
    }
    return null;
  }

  private lookupAccountBookingType(accountCode: string): string | null {
    if (this._accountLookup) {
      let account = this._accountLookup.getAccount(accountCode);
      if (account) {
        return CubiqTS.AccountBookingType[account.bookingType];
      }
    }
    return null;
  }

  private lookupClient(clientCode: string): CubiqTS.Client | null {
    if (this._clientLookup) {
      let client = this._clientLookup.getClient(clientCode);
      if (client) {
        return client;
      }
    }
    return null;
  }

  private lookupSalesGroup(salesGroupId: CubiqTS.Long): string | null {
    if (this._salesGroupLookup) {
      let sg = this._salesGroupLookup.getSalesGroup(salesGroupId);
      if (sg) {
        return sg.groupCode;
      }
    }
    return null;
  }

  private lookupTraderGroup(traderGroupId: CubiqTS.Long): string | null {
    if (this._traderGroupLookup) {
      let tg = this._traderGroupLookup.getTraderGroup(traderGroupId);
      if (tg) {
        return tg.groupCode;
      }
    }
    return null;
  }

  private truncateRateValue(
    rate: number,
    baseCode: string,
    nonBaseCode: string,
    isInvert: boolean,
    productType: string,
    orderType: CubiqTS.OrderType,
  ): string {
    if (
      typeof baseCode === "string" &&
      typeof nonBaseCode === "string" &&
      this._ccyPairLookupService.getCcyPair(baseCode, nonBaseCode) instanceof
        CubiqTS.CurrencyPairImpl
    ) {
      let rateText = rate.toString();
      let minPrecision = this.getMinPrecision(
        baseCode,
        nonBaseCode,
        isInvert,
        productType,
        orderType,
      );
      let decimalPointIndex = rateText.indexOf(".");
      if (
        rateText[rateText.length - 1] === "0" &&
        rateText.length - 1 - decimalPointIndex > minPrecision
      ) {
        // remove zeros at the end
        while (
          rateText[rateText.length - 1] === "0" &&
          rateText.length - 1 - decimalPointIndex > minPrecision
        ) {
          rateText = rateText.substring(0, rateText.length - 1);
        }
      } else if (rateText.length - 1 - decimalPointIndex < minPrecision) {
        // add zeros at the end
        while (rateText.length - 1 - decimalPointIndex < minPrecision) {
          rateText = rateText + "0";
        }
      }
      return rateText;
    }
    return rate.toString();
  }

  private formatRateValue(
    value: CubiqTS.BigNumber,
    baseCode: string,
    nonBaseCode: string,
    isInvert: boolean,
    productType: string,
    orderType: CubiqTS.OrderType,
  ): string {
    let currentDecs: number = value.decimalPlaces();

    if (
      typeof baseCode === "string" &&
      typeof nonBaseCode === "string" &&
      this._ccyPairLookupService.getCcyPair(baseCode, nonBaseCode) instanceof
        CubiqTS.CurrencyPairImpl
    ) {
      let minPrecision = this.getMinPrecision(
        baseCode,
        nonBaseCode,
        isInvert,
        productType,
        orderType,
      );

      if (currentDecs < minPrecision) {
        return value.toFixed(minPrecision);
      }
    }

    return value.toFixed(currentDecs);
  }

  private getMinPrecision(
    baseCode: string,
    nonBaseCode: string,
    isInvert: boolean,
    productType: string,
    orderType: CubiqTS.OrderType,
  ): number {
    let precision: number = 2;
    if (baseCode && nonBaseCode) {
      let ccyPair = this._ccyPairLookupService.getCcyPair(baseCode, nonBaseCode);
      if (ccyPair) {
        if (orderType === CubiqTS.OrderType.BENCHMARK || orderType === CubiqTS.OrderType.BMALGO) {
          if (!isInvert) {
            precision = ccyPair.bmSpotPrecision;
          } else if (isInvert) {
            precision = ccyPair.bmSpotInvPrecision;
          }
        } else {
          if (productType == "SPT" && !isInvert) {
            precision = ccyPair.rateDecimalsQty;
          } else if (productType == "SPT" && isInvert) {
            precision = ccyPair.spotInvertDecimalsQty;
          } else if (productType !== "SPT" && !isInvert) {
            precision = ccyPair.fwdRateDecimalsQty;
          } else if (productType !== "SPT" && isInvert) {
            precision = ccyPair.fwdInvertDecimalsQty;
          }
        }
      }
    }
    return precision;
  }

  private formatAmtValue(amt: CubiqTS.BigNumber, ccy: string) {
    let ccyPair = this._ccyLookupService.getCcy(ccy);
    if (amt != null && amt != undefined) {
      let precision: number = 0;
      let bigNum: CubiqTS.BigNumber = new CubiqTS.BigNumber(amt);
      if (ccyPair && bigNum.decimalPlaces() != 0) {
        precision = ccyPair.minorUnitsQty.toNumber();
      }
      return bigNum.toFormat(precision);
    }
    return "-";
  }

  reverseCcy(dealtCcy: string, baseCcyCd: string, nonBaseCcyCd: string) {
    if (dealtCcy === baseCcyCd) {
      return nonBaseCcyCd;
    } else if (dealtCcy === nonBaseCcyCd) {
      return baseCcyCd;
    }
  }
}
